// Microsoft Project XML export
// Compatible with MS Project, ProjectLibre, Smartsheet, Primavera P6 import

function normDep(dep) {
  if (typeof dep === 'string') return { id: dep, type: 'FS', lag: 0 };
  return { id: dep.id, type: dep.type || 'FS', lag: dep.lag || 0 };
}

// MS Project dependency type codes: FF=0, FS=1, SF=2, SS=3
const DEP_TYPE = { FF: 0, FS: 1, SF: 2, SS: 3 };

function esc(str) {
  return (str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// 'YYYY-MM-DD' → 'YYYY-MM-DDT08:00:00'
function isoStart(str) { return str ? `${str}T08:00:00` : ''; }
function isoFinish(str) { return str ? `${str}T17:00:00` : ''; }

// Calendar-day duration → MS Project duration string (P{n}DT0H0M0S)
function msDur(startStr, endStr) {
  if (!startStr || !endStr) return 'PT0H0M0S';
  const s = new Date(startStr), e = new Date(endStr);
  const days = Math.max(1, Math.round((e - s) / 86400000) + 1);
  return `P${days}DT0H0M0S`;
}

// Lag in days → LinkLag in tenths of a minute (MS Project schema definition)
// 1 working day = 8h = 480min = 4800 tenths
function lagTenths(days) { return (days || 0) * 4800; }

function taskXml(task, uid, outlineLevel, outlineNumber, taskUIDs) {
  const pct       = task.status === 'complete' ? 100 : (task.percentComplete ?? 0);
  const milestone = task.isMilestone ? 1 : 0;
  const dur       = milestone ? 'PT0H0M0S' : msDur(task.start, task.end);
  const finish    = milestone ? isoFinish(task.start) : isoFinish(task.end);

  let preds = '';
  (task.dependsOn || []).forEach(raw => {
    const dep     = normDep(raw);
    const predUID = taskUIDs[dep.id];
    if (predUID == null) return;
    preds += `      <PredecessorLink>
        <PredecessorUID>${predUID}</PredecessorUID>
        <Type>${DEP_TYPE[dep.type] ?? 1}</Type>
        <LinkLag>${lagTenths(dep.lag)}</LinkLag>
        <LagFormat>7</LagFormat>
      </PredecessorLink>\n`;
  });

  return `    <Task>
      <UID>${uid}</UID>
      <ID>${uid}</ID>
      <Name>${esc(task.name || '')}</Name>
      <WBS>${outlineNumber}</WBS>
      <OutlineNumber>${outlineNumber}</OutlineNumber>
      <OutlineLevel>${outlineLevel}</OutlineLevel>
      <Summary>0</Summary>
      <Milestone>${milestone}</Milestone>
      <Duration>${dur}</Duration>
      <DurationFormat>7</DurationFormat>
      <Start>${isoStart(task.start)}</Start>
      <Finish>${finish}</Finish>
      <PercentComplete>${pct}</PercentComplete>
      <PercentWorkComplete>${pct}</PercentWorkComplete>
${preds}    </Task>\n`;
}

export function buildGanttExportXml({ tasks, teams, phases, projectName, revisionLabel = null }) {
  const normPhases = (phases || []).map(p =>
    typeof p === 'string' ? { name: p } : p
  );
  const hasPhases = normPhases.length > 0;

  // Assign UIDs: phases first, then tasks (UID 0 = project summary, skip it)
  let nextUID = 1;
  const phaseUIDs    = {};
  const taskUIDs     = {};
  const resourceUIDs = {};

  if (hasPhases) {
    normPhases.forEach(p => { phaseUIDs[p.name] = nextUID++; });
    phaseUIDs['__ungrouped__'] = nextUID++;
  }
  tasks.forEach(t => { taskUIDs[t.id] = nextUID++; });
  teams.forEach((t, i) => { resourceUIDs[t.id || t.name] = i + 1; });

  // Group tasks by phase
  const byPhase   = {};
  const ungrouped = [];
  tasks.forEach(t => {
    if (hasPhases && t.phase && normPhases.some(p => p.name === t.phase)) {
      (byPhase[t.phase] = byPhase[t.phase] || []).push(t);
    } else {
      ungrouped.push(t);
    }
  });

  let tasksOut       = '';
  let assignmentsOut = '';
  let asgUID         = 1;

  const addAssignment = (task) => {
    const teamObj = teams.find(t => t.name === task.team);
    if (!teamObj) return;
    const rUID = resourceUIDs[teamObj.id || teamObj.name];
    if (rUID == null) return;
    const pct = task.status === 'complete' ? 100 : (task.percentComplete ?? 0);
    assignmentsOut += `    <Assignment>
      <UID>${asgUID++}</UID>
      <TaskUID>${taskUIDs[task.id]}</TaskUID>
      <ResourceUID>${rUID}</ResourceUID>
      <Units>1</Units>
      <PercentWorkComplete>${pct}</PercentWorkComplete>
    </Assignment>\n`;
  };

  if (hasPhases) {
    normPhases.forEach((phase, pi) => {
      const phaseTasks  = byPhase[phase.name] || [];
      const phaseOutNum = `${pi + 1}`;
      // Phase summary task
      tasksOut += `    <Task>
      <UID>${phaseUIDs[phase.name]}</UID>
      <ID>${phaseUIDs[phase.name]}</ID>
      <Name>${esc(phase.name)}</Name>
      <WBS>${phaseOutNum}</WBS>
      <OutlineNumber>${phaseOutNum}</OutlineNumber>
      <OutlineLevel>1</OutlineLevel>
      <Summary>1</Summary>
      <Milestone>0</Milestone>
      <PercentComplete>0</PercentComplete>
    </Task>\n`;
      phaseTasks.forEach((t, ti) => {
        tasksOut += taskXml(t, taskUIDs[t.id], 2, `${phaseOutNum}.${ti + 1}`, taskUIDs);
        addAssignment(t);
      });
    });

    if (ungrouped.length > 0) {
      const ugNum = `${normPhases.length + 1}`;
      tasksOut += `    <Task>
      <UID>${phaseUIDs['__ungrouped__']}</UID>
      <ID>${phaseUIDs['__ungrouped__']}</ID>
      <Name>Ungrouped</Name>
      <WBS>${ugNum}</WBS>
      <OutlineNumber>${ugNum}</OutlineNumber>
      <OutlineLevel>1</OutlineLevel>
      <Summary>1</Summary>
      <Milestone>0</Milestone>
      <PercentComplete>0</PercentComplete>
    </Task>\n`;
      ungrouped.forEach((t, ti) => {
        tasksOut += taskXml(t, taskUIDs[t.id], 2, `${ugNum}.${ti + 1}`, taskUIDs);
        addAssignment(t);
      });
    }
  } else {
    tasks.forEach((t, ti) => {
      tasksOut += taskXml(t, taskUIDs[t.id], 1, `${ti + 1}`, taskUIDs);
      addAssignment(t);
    });
  }

  let resourcesOut = '';
  teams.forEach((t, i) => {
    resourcesOut += `    <Resource>
      <UID>${i + 1}</UID>
      <ID>${i + 1}</ID>
      <Name>${esc(t.name)}</Name>
      <Type>1</Type>
    </Resource>\n`;
  });

  const now = new Date().toISOString().replace(/\.\d{3}Z$/, '');

  const xml = `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>
<Project xmlns="http://schemas.microsoft.com/project">
  <SaveVersion>14</SaveVersion>
  <Name>${esc(projectName)}</Name>
  <Title>${esc(projectName)}</Title>
  <CreationDate>${now}</CreationDate>
  <LastSaved>${now}</LastSaved>
  <ScheduleFromStart>1</ScheduleFromStart>
  <CalendarUID>1</CalendarUID>
  <DefaultStartTime>08:00:00</DefaultStartTime>
  <DefaultFinishTime>17:00:00</DefaultFinishTime>
  <MinutesPerDay>480</MinutesPerDay>
  <MinutesPerWeek>2400</MinutesPerWeek>
  <DaysPerMonth>20</DaysPerMonth>
  <Calendars>
    <Calendar>
      <UID>1</UID>
      <Name>Standard</Name>
      <IsBaseCalendar>1</IsBaseCalendar>
      <IsBaselineCalendar>0</IsBaselineCalendar>
    </Calendar>
  </Calendars>
  <Tasks>
${tasksOut}  </Tasks>
  <Resources>
${resourcesOut}  </Resources>
  <Assignments>
${assignmentsOut}  </Assignments>
</Project>`;

  const blob     = new Blob([xml], { type: 'application/xml;charset=utf-8' });
  const url      = URL.createObjectURL(blob);
  const a        = document.createElement('a');
  a.href         = url;
  const revMatch = revisionLabel?.match(/Rev ([A-Z]) \(Version (\d+)\)/);
  const safeRev  = revMatch ? `_Rev${revMatch[1]}_v${revMatch[2]}` : '';
  a.download = `${(projectName || 'schedule').replace(/[^a-z0-9]/gi, '_')}_programme${safeRev}.xml`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
